import { destroy, types, getParent } from 'mobx-state-tree';

import { Address, partiallyUpdateModel, removeEmptyProperties } from '../definitions/Common';
import { IntentTypeEnum } from '../definitions/IntentType';
import { PropertyTypeEnum } from '../definitions/PropertyType';
import { PropertyStyleEnum } from '../definitions/PropertyStyle';
import { ConstructionTypeEnum } from '../definitions/ConstructionType';
import { TenureTypeEnum } from '../definitions/TenureType';
import { HeatingTypeEnum } from '../definitions/HeatingType';
import { WaterSourceEnum } from '../definitions/WaterSource';
import { SewageTypeEnum } from '../definitions/SewageType';
import { PlumbingTypeEnum } from '../definitions/PlumbingType';
import { RoofTypeEnum } from '../definitions/RoofType';
import { ExteriorWallTypeEnum } from '../definitions/ExteriorWallType';
import { FoundationTypeEnum } from '../definitions/FoundationType';
import { GarageTypeEnum } from '../definitions/GarageType';
import { GarageSizeEnum } from '../definitions/GarageSize';
import { SwimmingPoolTypeEnum } from '../definitions/SwimmingPoolType';
import { BathroomTypeEnum } from '../definitions/BathroomType';
import { KitchenTypeEnum } from '../definitions/KitchenType';
import { AreaUnitEnum } from '../definitions/AreaUnit';
import { LivingSpaceUnitEnum } from '../definitions/LivingSpaceUnit';
import INTENDED_USE, { IntendedUseEnum } from '../definitions/IntendedUse';
import PURPOSES from '../definitions/Purpose';

import OptionalIdentifier from '../types/OptionalIdentifier';
import ContactPropertyMetadata from './ContactPropertyMetadata';
import Mortgage from './Mortgage';


const Property = types
    .model('Property', {
        id: OptionalIdentifier,
        address: types.optional(Address, {}),
        homeValue: types.maybe(types.number),
        replacementCost: types.maybe(types.number),
        propertyTax: types.maybe(types.number),
        monthlyRent: types.maybe(types.number),
        monthlyHeat: types.maybe(types.number),
        condoFees: types.maybe(types.number),
        propertyType: types.maybe(PropertyTypeEnum),
        style: types.maybe(PropertyStyleEnum),
        construction: types.maybe(ConstructionTypeEnum),
        age: types.maybe(types.integer),
        tenure: types.maybe(TenureTypeEnum),
        numberOfUnits: types.maybe(types.integer),
        heat: types.maybe(HeatingTypeEnum),
        auxiliaryHeatType: types.maybe(HeatingTypeEnum),
        waterSourceType: types.maybe(WaterSourceEnum),
        sewageType: types.maybe(SewageTypeEnum),
        plumbingType: types.maybe(PlumbingTypeEnum),
        roofType: types.maybe(RoofTypeEnum),
        exteriorWallType: types.maybe(ExteriorWallTypeEnum),
        foundationType: types.maybe(FoundationTypeEnum),
        garageType: types.maybe(GarageTypeEnum),
        garageSize: types.maybe(GarageSizeEnum),
        swimmingPoolType: types.maybe(SwimmingPoolTypeEnum),
        bathroomsType: types.maybe(BathroomTypeEnum),
        kitchensType: types.maybe(KitchenTypeEnum),
        finishedBasement: types.maybe(types.boolean),
        livingSpace: types.maybe(types.number),
        livingSpaceUnit: types.maybe(LivingSpaceUnitEnum),
        lotSize: types.maybe(types.number),
        lotSizeUnit: types.maybe(AreaUnitEnum),
        commercialIndicator: types.maybe(types.boolean),
        environmentalHazard: types.maybe(types.boolean),
        contactPropertyMetadata: types.array(types.maybe(ContactPropertyMetadata)), // TODO: rename plural?
        intendedUse: types.maybe(IntendedUseEnum),
        intentType: types.maybe(IntentTypeEnum),
        iClarifyID: types.maybe(types.string),
        existingMortgage: types.maybe(Mortgage),
    })
    .views(self => ({
        contactResidence(contact) {
            return self.contactPropertyMetadata
                .find(({ contact: metaContact, livesAt }) =>
                    metaContact === contact && livesAt);
        },
        contactOwnership(contact) {
            return self.contactPropertyMetadata
                .find(({ contact: metaContact, ownProperty }) =>
                    metaContact === contact && ownProperty);
        },
        getMetadataByContact(contact) {
            return self.contactPropertyMetadata.find(({ contact: metaContact }) => metaContact === contact);
        },
        get journey() {
            // ideally we'd use getParentOfType(Journey) here but this brings a circular reference
            let current = self;
            while (!current.subjectProperty) {
                current = getParent(current);
            }
            return current;
        },
        get isSubjectProperty() {
            return self === self.journey.subjectProperty;
        },
        get isOwnerOccupied() {
            if (!self.intendedUse) {
                return null;
            }

            return [ INTENDED_USE.OWNER_OCCUPIED, INTENDED_USE.OWNER_OCCUPIED_RENTAL ].includes(self.intendedUse);
        },
    }))
    .actions(self => ({
        setContactResidence({ contact, livesAt, ...snapshot }) {
            const existingResidence = self.contactResidence(contact);
            const existingOwnership = self.contactOwnership(contact);
            const existingMetadata = existingOwnership || existingResidence;

            if (livesAt || typeof livesAt === 'undefined') {
                existingMetadata
                    ? partiallyUpdateModel(existingMetadata, { ...snapshot, livesAt: true })
                    : self.contactPropertyMetadata.push({
                        ...removeEmptyProperties(snapshot),
                        contact: contact.id,
                        livesAt: true,
                    });

                const { ownProperty, hasMortgage } = snapshot;
                if (typeof ownProperty !== 'undefined') {
                    self.setContactOwnership({ contact, ownProperty, hasMortgage });
                }
            } else {
                if (existingResidence) {
                    // Possible for a single metadata entry to be both for a currently-owned residence
                    existingResidence === existingOwnership
                        ? Object.assign(existingResidence,
                            { livesAt: false, livesWithParents: false, currentAddress: true, startDate: undefined, endDate: undefined })
                        : destroy(existingResidence);
                }
            }

            // no metadata, abort to prevent out of bounds array access on next line
            if (!self.contactPropertyMetadata.length) {
                return;
            }

            const isContactPropertyRecordOwner = existingResidence === self.contactPropertyMetadata[0];
            if (isContactPropertyRecordOwner) {
                self.contactPropertyMetadata
                    .filter((m, index) => index > 0 && m.livesAt)
                    .forEach(m => self.setContactResidence({ contact: m.contact, livesAt }));
            }
        },
        setContactOwnership({ contact, ownProperty, ...snapshot }) {
            const existingResidence = self.contactResidence(contact);
            const existingOwnership = self.contactOwnership(contact);
            const existingMetadata = existingOwnership || existingResidence;

            if (ownProperty || typeof ownProperty === 'undefined') {
                existingMetadata
                    ? partiallyUpdateModel(existingMetadata, { ...snapshot, ownProperty: true })
                    : self.contactPropertyMetadata.push({
                        ...(!existingResidence && { currentAddress: true }),
                        ...removeEmptyProperties(snapshot),
                        contact: contact.id,
                        ownProperty: true,
                    });
            } else {
                if (existingOwnership) {
                    // Possible for a single metadata entry to be both for a currently-owned residence
                    existingOwnership === existingResidence
                        ? partiallyUpdateModel(existingOwnership, { ownProperty: false, hasMortgage: false })
                        : destroy(existingOwnership);
                }
            }

            const isContactPropertyRecordOwner = existingOwnership === self.contactPropertyMetadata[0];
            if (isContactPropertyRecordOwner) {
                self.contactPropertyMetadata
                    .filter((m, index) => index > 0 && m.ownProperty)
                    .forEach(m => self.setContactOwnership({ contact: m.contact, ownProperty, ...snapshot }));
            }
        },
        setMortgage(snapshot = {}) {
            if (self.existingMortgage) {
                partiallyUpdateModel(self.existingMortgage, snapshot);
            } else {
                self.existingMortgage = removeEmptyProperties(snapshot);
            }
            if (snapshot.renewalDate && self.isSubjectProperty) {
                const journey = self.journey;
                if (journey.purpose === PURPOSES.RENEWAL) {
                    journey.update({ application: { closingDate: snapshot.renewalDate } });
                }
            }
        },
        removeMortgage() {
            if (self.mortgage) {
                destroy(self.existingMortgage);
            }
        },
        removeContactResidence(contact) {
            self.setContactResidence({ contact, livesAt: false });
        },
        removeContactOwnership(contact) {
            self.setContactOwnership({ contact, ownProperty: false });
        },
    }));

export default Property;
